import type { ReactNode } from 'react';

// Shape of the per-platform JSON returned by Claude. Different platforms
// produce different fields — we read what's there and render the rest as
// fallback. See backend/services/prompts.ts for the prompts that produce
// these shapes.
interface ContentShape {
  body?: string;
  hashtags?: string[];
  title?: string;
  tweet?: string;
  thread?: string[];
  outline?: string[];
  intro?: string;
  suggestedSubreddits?: string[];
}

function formatHashtags(hashtags: string[] | undefined): string | null {
  if (!hashtags || hashtags.length === 0) return null;
  return hashtags.map((h) => (h.startsWith('#') ? h : `#${h}`)).join(' ');
}

const proseStyle = { whiteSpace: 'pre-wrap' as const, margin: 0 };
const sectionStyle = { marginTop: '0.5rem' };
const dividerStyle = {
  marginTop: '0.5rem',
  paddingLeft: '0.75rem',
  borderLeft: '2px solid var(--border)',
};

// Render the Claude output as readable text — never raw JSON. Falls back to
// a stringified blob if the platform isn't recognised so the user can still
// see what came back.
export function formatContent(
  platform: string,
  content: Record<string, unknown>,
): ReactNode {
  const c = content as ContentShape;

  switch (platform) {
    case 'linkedin':
    case 'facebook':
    case 'discord': {
      const tags = formatHashtags(c.hashtags);
      return (
        <div>
          {c.body && <p style={proseStyle}>{c.body}</p>}
          {tags && <p className="muted small" style={sectionStyle}>{tags}</p>}
        </div>
      );
    }

    case 'reddit':
      return (
        <div>
          {c.title && (
            <p style={{ ...proseStyle, fontWeight: 600 }}>{c.title}</p>
          )}
          {c.body && <p style={{ ...proseStyle, ...sectionStyle }}>{c.body}</p>}
          {c.suggestedSubreddits && c.suggestedSubreddits.length > 0 && (
            <p className="muted small" style={sectionStyle}>
              Subreddits: {c.suggestedSubreddits.map((s) =>
                s.startsWith('r/') ? s : `r/${s}`,
              ).join(', ')}
            </p>
          )}
        </div>
      );

    case 'twitter': {
      const tags = formatHashtags(c.hashtags);
      const total = (c.thread?.length ?? 0) + (c.tweet ? 1 : 0);
      return (
        <div>
          {c.tweet && (
            <p style={proseStyle}>
              <span className="muted small">1/{total} </span>
              {c.tweet}
            </p>
          )}
          {c.thread?.map((t, i) => (
            <p key={i} style={{ ...proseStyle, ...dividerStyle }}>
              <span className="muted small">{i + 2}/{total} </span>
              {t}
            </p>
          ))}
          {tags && <p className="muted small" style={sectionStyle}>{tags}</p>}
        </div>
      );
    }

    case 'medium':
      return (
        <div>
          {c.title && (
            <p style={{ ...proseStyle, fontWeight: 600 }}>{c.title}</p>
          )}
          {c.outline && c.outline.length > 0 && (
            <ol style={{ paddingLeft: '1.2rem', marginTop: '0.5rem' }}>
              {c.outline.map((o, i) => (
                <li key={i} className="small">{o}</li>
              ))}
            </ol>
          )}
          {c.intro && <p style={{ ...proseStyle, ...sectionStyle }}>{c.intro}</p>}
        </div>
      );

    default:
      return <pre style={proseStyle}>{JSON.stringify(content, null, 2)}</pre>;
  }
}

// Plain-text version used for the "copy" action — gives the user something
// they can paste into the actual social platform.
export function contentToPlainText(
  platform: string,
  content: Record<string, unknown>,
): string {
  const c = content as ContentShape;
  const parts: string[] = [];

  switch (platform) {
    case 'linkedin':
    case 'facebook':
    case 'discord': {
      if (c.body) parts.push(c.body);
      const tags = formatHashtags(c.hashtags);
      if (tags) parts.push(tags);
      return parts.join('\n\n');
    }
    case 'reddit': {
      if (c.title) parts.push(c.title);
      if (c.body) parts.push(c.body);
      if (c.suggestedSubreddits && c.suggestedSubreddits.length > 0) {
        parts.push(
          `Subreddits: ${c.suggestedSubreddits
            .map((s) => (s.startsWith('r/') ? s : `r/${s}`))
            .join(', ')}`,
        );
      }
      return parts.join('\n\n');
    }
    case 'twitter': {
      if (c.tweet) parts.push(c.tweet);
      if (c.thread) parts.push(...c.thread);
      const tags = formatHashtags(c.hashtags);
      if (tags) parts.push(tags);
      return parts.join('\n\n');
    }
    case 'medium': {
      if (c.title) parts.push(c.title);
      if (c.outline && c.outline.length > 0) {
        parts.push(c.outline.map((o, i) => `${i + 1}. ${o}`).join('\n'));
      }
      if (c.intro) parts.push(c.intro);
      return parts.join('\n\n');
    }
    default:
      return JSON.stringify(content, null, 2);
  }
}
